// Reading a PDF someone sent, without leaving the conversation. The document is a
// blob fetched through the gated endpoint a moment ago — no URL to the file
// exists outside this dialog, and it is released when the dialog closes.
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Download } from 'lucide-react';

export interface PdfPreviewDialogProps {
  /** An object URL for the fetched PDF blob. Absent while an Office file's PDF
   *  is being prepared, or when there is none: `children` say why instead. */
  src?: string;
  children?: React.ReactNode;
  /** Shown above the document or the message, whichever is open: e.g. a
   *  failed Download, which must be seen without closing the PDF. */
  notice?: React.ReactNode;
  name: string;
  downloadLabel?: string;
  closeLabel?: string;
  onDownload: () => void;
  onClose: () => void;
}

export const PdfPreviewDialog: React.FC<PdfPreviewDialogProps> = ({
  src, children, notice, name, downloadLabel = 'Download', closeLabel = 'Close', onDownload, onClose,
}) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={name}
      onClick={onClose}
      className="fixed inset-0 z-[1100] flex flex-col bg-black/85 p-2 backdrop-blur-sm sm:p-4"
    >
      <div className="mb-2 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        <p className="min-w-0 flex-1 truncate text-sm font-medium text-white">{name}</p>
        <button
          type="button"
          onClick={onDownload}
          aria-label={downloadLabel}
          title={downloadLabel}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
        >
          <Download className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={onClose}
          autoFocus
          aria-label={closeLabel}
          title={closeLabel}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      {notice && (
        <div className="mb-2" onClick={(e) => e.stopPropagation()}>
          {notice}
        </div>
      )}
      {src ? (
        <iframe
          src={src}
          title={name}
          onClick={(e) => e.stopPropagation()}
          className="min-h-0 w-full flex-1 rounded-lg bg-card"
        />
      ) : (
        <div
          onClick={(e) => e.stopPropagation()}
          className="flex min-h-0 w-full flex-1 items-center justify-center rounded-lg bg-card p-6 text-center"
        >
          <div className="max-w-sm space-y-3 text-sm text-foreground">{children}</div>
        </div>
      )}
    </div>,
    document.body,
  );
};

export default PdfPreviewDialog;
